import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Header from '../Common/Header';
import Footer from '../Common/Footer';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
const PAYMENT_ENDPOINT = process.env.REACT_APP_PAYMENT_ENDPOINT;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL;

// Prefijo de las claves i18n de esta pantalla (viven bajo pages.pricing.checkout).
const CK = 'pages.pricing.checkout';

const PRICES = {
    analysis_plan:    { usd: 499,  eur: 499,  mxn: 9980 },
    architect_review: { usd: 4600, eur: 4600, mxn: 82000 },
    hosted_db: {
        monthly: { usd: 38,  eur: 35,  mxn: 750 },
        yearly:  { usd: 240, eur: 218, mxn: 4788 },
    },
    private_db: {
        monthly: { usd: 48,  eur: 44,  mxn: 950 },
        yearly:  { usd: 360, eur: 327, mxn: 7188 },
    },
    bring_your_own_db: {
        monthly: { usd: 28,  eur: 25,  mxn: 550 },
        yearly:  { usd: 180, eur: 164, mxn: 3588 },
    },
    // Planes por nº de proyectos (precios base MXN; USD = MXN/20, EUR = MXN/22).
    plan5: {
        monthly: { usd: 22.5,   eur: 20.45,  mxn: 450 },
        yearly:  { usd: 144.9,  eur: 131.73, mxn: 2898 },
    },
    plan10: {
        monthly: { usd: 31.5,   eur: 28.64,  mxn: 630 },
        yearly:  { usd: 189.9,  eur: 172.64, mxn: 3798 },
    },
    plan20: {
        monthly: { usd: 42.5,   eur: 38.64,  mxn: 850 },
        yearly:  { usd: 256.45, eur: 233.14, mxn: 5129 },
    },
    // Instancia dedicada: 3 tamaños de base de datos (se elige con ?tier=0|1|2).
    dedicated: {
        tiers: [
            { monthly: { usd: 57.6,  eur: 52.36,  mxn: 1152 }, yearly: { usd: 357.8,  eur: 325.27, mxn: 7156 } },
            { monthly: { usd: 79.45, eur: 72.23,  mxn: 1589 }, yearly: { usd: 541.15, eur: 491.95, mxn: 10823 } },
            { monthly: { usd: 112.8, eur: 102.55, mxn: 2256 }, yearly: { usd: 905.1,  eur: 822.82, mxn: 18102 } },
        ],
    },
    // Plan de prueba (no público; solo deep-link ?plan=test). Mínimos de cobro Stripe.
    test: {
        monthly: { usd: 0.50, eur: 0.50, mxn: 10 },
    },
};

// Fallback de nombres de plan si faltara la clave i18n (checkout.planNames.<id>).
const PLAN_NAME_FALLBACK = {
    analysis_plan: 'AI Analysis Plan',
    architect_review: 'Architect Review',
    hosted_db: 'Hosted DB',
    private_db: 'Instancia Privada',
    bring_your_own_db: 'Tu Propia DB',
    plan5: '5 Proyectos',
    plan10: '10 Proyectos',
    plan20: '20 Proyectos',
    dedicated: 'Instancia Dedicada',
    test: 'Test Plan',
};

const CURRENCY_SYMBOL = { usd: '$', eur: '€', mxn: '$' };
const CURRENCY_LABEL = { usd: 'USD', eur: 'EUR', mxn: 'MXN' };

function resolvePrice(plan, billing, currency, tierIdx = 0) {
    let entry = PRICES[plan];
    if (!entry) return 0;
    if (entry.tiers) entry = entry.tiers[tierIdx] ?? entry.tiers[0]; // instancia dedicada
    const sel = entry.monthly ? (entry[billing] ?? entry.monthly) : entry;
    return sel?.[currency] ?? 0;
}

/* ── styles ── */
const inputStyle = (hasError) => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.75rem 1rem',
    border: `1px solid ${hasError ? '#ef4444' : 'var(--line)'}`,
    borderRadius: '8px',
    fontSize: '0.9375rem',
    fontFamily: 'inherit',
    outline: 'none',
    color: 'var(--ink)',
    background: '#fff',
});

const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: 'var(--ink)',
    marginBottom: '0.5rem',
};

const btnStyle = (disabled) => ({
    width: '100%',
    padding: '0.9375rem',
    background: disabled ? 'var(--ink-muted)' : 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.2s',
});

/* ══════════════════════════════════════════════════════
   Inner payment form — must be a child of <Elements>
══════════════════════════════════════════════════════ */
function StripePaymentForm({ plan, currency, email, planName, price, symbol, currencyLabel, periodLong, onSuccess }) {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');

    const handlePay = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setError('');
        setProcessing(true);

        const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: { receipt_email: email },
        });

        if (stripeError) {
            setError(stripeError.message);
            setProcessing(false);
            return;
        }

        setProcessing(false);
        onSuccess(paymentIntent?.id ?? '');
    };

    return (
        <form onSubmit={handlePay} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <PaymentElement />

            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem 1rem', color: '#dc2626', fontSize: '0.875rem' }}>
                    {error}
                </div>
            )}

            <button type="submit" disabled={!stripe || processing} style={btnStyle(!stripe || processing)}>
                {processing
                    ? t(`${CK}.payment.processing`)
                    : t(`${CK}.payment.payButton`, { symbol, price: price.toLocaleString(), currency: currencyLabel, period: periodLong })}
            </button>

            <p style={{ textAlign: 'center', fontSize: '0.8125rem', color: 'var(--ink-muted)', margin: 0, lineHeight: '1.6' }}>
                {t(`${CK}.payment.agree`)}
                <a href="/terminos-y-condiciones" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{t(`${CK}.payment.terms`)}</a>
                {t(`${CK}.payment.and`)}
                <a href="/declaracion-de-privacidad" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{t(`${CK}.payment.privacy`)}</a>
            </p>
        </form>
    );
}

/* ══════════════════════════════════════════════════════
   Password policy — shown live under the field + enforced on submit
══════════════════════════════════════════════════════ */
// "No números seguidos": rechaza 3+ dígitos consecutivos en secuencia
// ascendente o descendente (123, 234, 987, …). No mira dígitos repetidos.
function hasSequentialDigits(p, run = 3) {
    let asc = 1, desc = 1;
    for (let i = 1; i < p.length; i++) {
        const prev = p[i - 1], cur = p[i];
        const bothDigits = prev >= '0' && prev <= '9' && cur >= '0' && cur <= '9';
        const d = cur.charCodeAt(0) - prev.charCodeAt(0);
        asc  = bothDigits && d === 1  ? asc + 1  : 1;
        desc = bothDigits && d === -1 ? desc + 1 : 1;
        if (asc >= run || desc >= run) return true;
    }
    return false;
}

// Reglas de contraseña: la etiqueta se traduce por `key` (passwordRules.<key>).
const PASSWORD_RULES = [
    { key: 'len',   test: (p) => p.length >= 8 },
    { key: 'upper', test: (p) => /[A-Z]/.test(p) },
    { key: 'alnum', test: (p) => /[a-zA-Z]/.test(p) && /\d/.test(p) },
    { key: 'noseq', test: (p) => p.length > 0 && !hasSequentialDigits(p) },
];

const passwordIsValid = (p) => PASSWORD_RULES.every((r) => r.test(p));

function PasswordRequirements({ value }) {
    const { t } = useTranslation();
    return (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {PASSWORD_RULES.map(({ key, test }) => {
                const ok = test(value);
                return (
                    <li key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8125rem', color: ok ? '#16a34a' : 'var(--ink-muted)' }}>
                        <span aria-hidden="true" style={{ display: 'inline-flex', width: '1rem', justifyContent: 'center', fontWeight: 700 }}>{ok ? '✓' : '○'}</span>
                        {t(`${CK}.register.passwordRules.${key}`)}
                    </li>
                );
            })}
        </ul>
    );
}

/* ══════════════════════════════════════════════════════
   Registration form — shown after successful payment
══════════════════════════════════════════════════════ */
function RegistrationForm({ plan, planName, prefillEmail, paymentId, billing }) {
    const { t, i18n } = useTranslation();
    const [form, setForm] = useState({ full_name: '', username: '', email: prefillEmail || '', password: '', confirm: '' });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState('');
    const [done, setDone] = useState(false);
    // Idioma para los datos de ejemplo/plantilla que se siembran al crear la cuenta.
    // Default = idioma del sitio si es es/en (fr → es); el admin lo puede cambiar.
    // Se envía al backend y se propaga al dashboard vía ?lang=.
    const [seedLang, setSeedLang] = useState(i18n.language && i18n.language.startsWith('en') ? 'en' : 'es');

    const change = (field) => (e) => {
        setForm({ ...form, [field]: e.target.value });
        setErrors({ ...errors, [field]: '' });
    };

    const validate = () => {
        const e = {};
        if (!form.full_name.trim()) e.full_name = t(`${CK}.register.errors.fullNameRequired`);
        if (!form.username.trim()) e.username = t(`${CK}.register.errors.usernameRequired`);
        else if (!/^[a-zA-Z0-9_.-]{3,}$/.test(form.username)) e.username = t(`${CK}.register.errors.usernameInvalid`);
        if (!form.email.trim()) e.email = t(`${CK}.register.errors.emailRequired`);
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t(`${CK}.register.errors.emailInvalid`);
        if (!form.password) e.password = t(`${CK}.register.errors.passwordRequired`);
        else if (!passwordIsValid(form.password)) e.password = t(`${CK}.register.errors.passwordWeak`);
        if (!form.confirm) e.confirm = t(`${CK}.register.errors.confirmRequired`);
        else if (form.password !== form.confirm) e.confirm = t(`${CK}.register.errors.passwordMismatch`);
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setLoading(true);
        setApiError('');
        try {
            const res = await fetch(`${BACKEND_URL}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email:          form.email,
                    username:       form.username,
                    password:       form.password,
                    full_name:      form.full_name,
                    payment_id:     paymentId,
                    payment_plan:   plan,
                    payment_method: 'card',
                    billing_period: billing,
                    seed_lang:      seedLang,
                }),
            });
            const text = await res.text();
            const data = text ? JSON.parse(text) : null;
            if (!res.ok) { setApiError(data?.detail || data?.message || t(`${CK}.register.registrationFailed`)); setLoading(false); return; }
            setDone(true);
        } catch {
            setApiError(t(`${CK}.register.registrationFailedRetry`));
        }
        setLoading(false);
    };

    if (done) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ width: '56px', height: '56px', background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
                    <svg style={{ width: '28px', height: '28px', color: '#22c55e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--ink)', margin: '0 0 0.5rem' }}>{t(`${CK}.register.accountCreated`)}</h3>
                <p style={{ color: 'var(--ink-muted)', margin: '0 0 1.5rem' }}>{t(`${CK}.register.accountCreatedText`)}</p>
                <button onClick={() => { window.location.href = `${DASHBOARD_URL}?lang=${seedLang}`; }} style={{ ...btnStyle(false), width: 'auto', padding: '0.75rem 2rem' }}>
                    {t(`${CK}.register.goToDashboard`)}
                </button>
            </div>
        );
    }

    const fields = [
        { field: 'full_name', label: t(`${CK}.register.fullName`),     placeholder: t(`${CK}.register.fullNamePlaceholder`), type: 'text' },
        { field: 'username',  label: t(`${CK}.register.username`),     placeholder: t(`${CK}.register.usernamePlaceholder`), type: 'text' },
        { field: 'email',     label: t(`${CK}.register.email`),        placeholder: t(`${CK}.register.emailPlaceholder`),    type: 'email' },
        { field: 'password',  label: t(`${CK}.register.password`),     placeholder: t(`${CK}.register.passwordPlaceholder`), type: 'password' },
        { field: 'confirm',   label: t(`${CK}.register.confirm`),      placeholder: t(`${CK}.register.confirmPlaceholder`),  type: 'password' },
    ];

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {fields.map(({ field, label, placeholder, type }) => (
                <div key={field}>
                    <label style={labelStyle}>{label} <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type={type} value={form[field]} onChange={change(field)} placeholder={placeholder} style={inputStyle(!!errors[field])} />
                    {errors[field] && <p style={{ color: '#ef4444', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>{errors[field]}</p>}
                    {field === 'password' && <PasswordRequirements value={form.password} />}
                </div>
            ))}

            <div style={{ background: 'var(--bg)', borderRadius: '8px', padding: '0.875rem 1rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--ink-muted)' }}>{t(`${CK}.register.selectedPlan`)}</span>
                <span style={{ fontWeight: '600', color: 'var(--accent)', fontSize: '0.875rem' }}>{planName}</span>
            </div>

            <div>
                <label style={labelStyle}>{t(`${CK}.register.seedLangLabel`)}</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[
                        { code: 'es', label: 'Español' },
                        { code: 'en', label: 'English' },
                    ].map(({ code, label }) => (
                        <button
                            key={code}
                            type="button"
                            onClick={() => setSeedLang(code)}
                            style={{
                                flex: 1,
                                padding: '0.625rem 1rem',
                                borderRadius: '8px',
                                border: seedLang === code ? '2px solid var(--accent)' : '1px solid #d1d5db',
                                background: seedLang === code ? '#fdf8f3' : 'var(--bg)',
                                color: 'var(--ink)',
                                fontWeight: seedLang === code ? '600' : '400',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <p style={{ color: 'var(--ink-muted)', fontSize: '0.8125rem', margin: '0.375rem 0 0' }}>
                    {t(`${CK}.register.seedLangHelp`)}
                </p>
            </div>

            {plan === 'architect_review' && (
                <div style={{ background: '#fdf8f3', border: '1px solid var(--accent)', borderRadius: '8px', padding: '0.875rem 1rem' }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                        <strong style={{ color: 'var(--ink)' }}>{t(`${CK}.register.premiumBenefit`)}</strong>{t(`${CK}.register.premiumBenefitText`)}
                    </p>
                </div>
            )}

            {apiError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem 1rem', color: '#dc2626', fontSize: '0.875rem' }}>
                    {apiError}
                </div>
            )}

            <button type="submit" disabled={loading} style={btnStyle(loading)}>
                {loading ? t(`${CK}.register.creating`) : t(`${CK}.register.createButton`)}
            </button>
        </form>
    );
}

/* ══════════════════════════════════════════════════════
   Main checkout page
══════════════════════════════════════════════════════ */
function PricingCheckout() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const plan = searchParams.get('plan') || 'analysis_plan';
    const currency = searchParams.get('currency') || 'usd';
    const billing = searchParams.get('billing') || 'monthly';
    const tier = Number(searchParams.get('tier') || 0);

    const price = resolvePrice(plan, billing, currency, tier);
    const symbol = CURRENCY_SYMBOL[currency] ?? '$';
    const currencyLabel = CURRENCY_LABEL[currency] ?? 'USD';
    const planName = t(`${CK}.planNames.${plan}`, PLAN_NAME_FALLBACK[plan] ?? plan);
    const isYearly = billing === 'yearly';
    const periodNoun = isYearly ? t(`${CK}.annualSubscription`) : t(`${CK}.monthlySubscription`);
    const periodShort = isYearly ? t(`${CK}.periodShort.year`) : t(`${CK}.periodShort.month`);
    const periodLong = isYearly ? t(`${CK}.period.year`) : t(`${CK}.period.month`);

    /* email step */
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    /* stripe state */
    const [clientSecret, setClientSecret] = useState('');
    const [apiError, setApiError] = useState('');
    const [loadingIntent, setLoadingIntent] = useState(false);
    const [isExistingUser, setIsExistingUser] = useState(false);

    /* flow stages: 'email' | 'payment' | 'register' */
    const [stage, setStage] = useState('email');
    const [paymentId, setPaymentId] = useState('');

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setEmailError('');
        setApiError('');
        setIsExistingUser(false);
    };

    const createPaymentIntent = async () => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError(t(`${CK}.email.invalidEmail`));
            return;
        }
        setEmailError('');
        setApiError('');
        setIsExistingUser(false);
        setLoadingIntent(true);

        try {
            // El payments_backend separa mensual/anual por key (`plan5` vs `plan5_yearly`)
            // y NO recibe `billing`. Mandamos el key con sufijo en anual (solo para los
            // planes que tienen variante anual, para no romper planes one-time).
            // La instancia dedicada además define la key por TAMAÑO de base de datos (tier):
            // tier 0 → 'dedicated', 1 → 'dedicated_2g', 2 → 'dedicated_4g'.
            const YEARLY_CAPABLE = new Set(['plan5', 'plan10', 'plan20', 'dedicated']);
            const DEDICATED_BY_TIER = ['dedicated', 'dedicated_2g', 'dedicated_4g'];
            const base = plan === 'dedicated' ? (DEDICATED_BY_TIER[tier] || 'dedicated') : plan;
            const paymentPlan = (billing === 'yearly' && YEARLY_CAPABLE.has(plan)) ? `${base}_yearly` : base;

            const res = await fetch(PAYMENT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currency, email, plan: paymentPlan, time_zone: userTimezone }),
            });

            const text = await res.text();
            let body = null;
            try { body = text ? JSON.parse(text) : null; } catch { /* non-JSON body */ }

            if (!res.ok) {
                const serverMsg = body?.detail || body?.message || body?.error || null;
                const looksLikeExisting = serverMsg
                    ? /exist|already|duplicate|registered/i.test(serverMsg)
                    : false;

                if (looksLikeExisting) {
                    setIsExistingUser(true);
                    setApiError(t(`${CK}.email.existingAccount`, { email }));
                } else {
                    setApiError(serverMsg || t(`${CK}.email.genericError`, { status: res.status }));
                }
                setLoadingIntent(false);
                return;
            }

            setClientSecret(body.clientSecret);
            setStage('payment');
        } catch (err) {
            setApiError(t(`${CK}.email.networkError`));
        }
        setLoadingIntent(false);
    };

    const stripeAppearance = { theme: 'stripe' };

    /* ── layout shell ── */
    const shell = (content) => (
        <>
            <Header />
            <div className="page-content" style={{ background: 'var(--bg)', minHeight: '80vh', padding: '7rem 1.5rem 5rem' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <button
                        onClick={() => navigate('/pricing')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', color: 'var(--ink-muted)', cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit', padding: 0, marginBottom: '2rem' }}
                    >
                        <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        {t(`${CK}.back`)}
                    </button>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', alignItems: 'start' }}>
                        {/* Order Summary — always visible */}
                        <div style={{ background: '#fff', borderRadius: '16px', padding: '2rem', border: '1px solid var(--line)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--ink)', margin: '0 0 1.5rem' }}>{t(`${CK}.orderSummary`)}</h2>
                            <div style={{ paddingTop: '1.5rem', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <p style={{ fontWeight: '600', color: 'var(--ink)', margin: 0 }}>{planName}</p>
                                    <p style={{ fontSize: '0.8125rem', color: 'var(--ink-muted)', margin: '0.25rem 0 0' }}>{periodNoun}</p>
                                </div>
                                <span style={{ fontWeight: '700', color: 'var(--ink)' }}>{symbol}{price.toLocaleString()}</span>
                            </div>
                            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: '600', color: 'var(--ink)' }}>{t(`${CK}.total`)}</span>
                                <span style={{ fontSize: '1.375rem', fontWeight: '700', color: 'var(--ink)' }}>
                                    {symbol}{price.toLocaleString()}{' '}
                                    <span style={{ fontSize: '0.875rem', fontWeight: '400', color: 'var(--ink-muted)' }}>{currencyLabel}/{periodShort}</span>
                                </span>
                            </div>
                            <div style={{ marginTop: '1.5rem', background: 'var(--bg)', borderRadius: '10px', padding: '1rem' }}>
                                <p style={{ fontSize: '0.8125rem', color: 'var(--ink-muted)', margin: 0, lineHeight: '1.6' }}>
                                    {t(`${CK}.includeNote`)}
                                </p>
                            </div>
                        </div>

                        {/* Right panel */}
                        <div style={{ background: '#fff', borderRadius: '16px', padding: '2rem', border: '1px solid var(--line)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            {content}
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );

    /* ── Stage: email entry ── */
    if (stage === 'email') {
        return shell(
            <>
                <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--ink)', margin: '0 0 1.5rem' }}>{t(`${CK}.email.title`)}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    <div>
                        <label style={labelStyle}>{t(`${CK}.email.emailLabel`)} <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !isExistingUser) createPaymentIntent(); }}
                            placeholder={t(`${CK}.email.emailPlaceholder`)}
                            style={inputStyle(!!emailError)}
                        />
                        {emailError && <p style={{ color: '#ef4444', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>{emailError}</p>}
                    </div>

                    {apiError && (
                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.875rem 1rem', color: '#dc2626', fontSize: '0.875rem' }}>
                            {apiError}
                            {isExistingUser && (
                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>
                                    {t(`${CK}.email.useDifferentOr`)}
                                    <a href={DASHBOARD_URL} style={{ color: '#dc2626', fontWeight: '600', textDecoration: 'underline' }}>
                                        {t(`${CK}.email.goToDashboardLink`)}
                                    </a>
                                    {t(`${CK}.email.useDifferentEnd`)}
                                </p>
                            )}
                        </div>
                    )}

                    {isExistingUser ? (
                        <a
                            href={DASHBOARD_URL}
                            style={{ ...btnStyle(false), display: 'block', textAlign: 'center', textDecoration: 'none', background: '#1a1a1a' }}
                        >
                            {t(`${CK}.email.goToDashboard`)}
                        </a>
                    ) : (
                        <button onClick={createPaymentIntent} disabled={loadingIntent} style={btnStyle(loadingIntent)}>
                            {loadingIntent ? t(`${CK}.email.pleaseWait`) : t(`${CK}.email.continue`)}
                        </button>
                    )}
                </div>
            </>
        );
    }

    /* ── Stage: Stripe payment form ── */
    if (stage === 'payment' && clientSecret) {
        return shell(
            <>
                <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--ink)', margin: '0 0 0.25rem' }}>{t(`${CK}.payment.title`)}</h2>
                <p style={{ fontSize: '0.8125rem', color: 'var(--ink-muted)', margin: '0 0 1.5rem' }}>{email}</p>
                <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
                    <StripePaymentForm
                        plan={plan}
                        currency={currency}
                        email={email}
                        planName={planName}
                        price={price}
                        symbol={symbol}
                        currencyLabel={currencyLabel}
                        periodLong={periodLong}
                        onSuccess={(id) => { setPaymentId(id); setStage('register'); }}
                    />
                </Elements>
            </>
        );
    }

    /* ── Stage: registration after payment ── */
    if (stage === 'register') {
        return shell(
            <>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                    <div style={{ width: '48px', height: '48px', background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem' }}>
                        <svg style={{ width: '24px', height: '24px', color: '#22c55e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--ink)', margin: '0 0 0.25rem' }}>{t(`${CK}.success.title`)}</h2>
                    <p style={{ color: 'var(--ink-muted)', margin: 0, fontSize: '0.875rem' }}>{planName} — {symbol}{price.toLocaleString()} {currencyLabel}</p>
                </div>
                <div style={{ borderTop: '1px solid var(--line)', paddingTop: '1.5rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--ink)', margin: '0 0 0.25rem' }}>{t(`${CK}.success.createTitle`)}</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--ink-muted)', margin: '0 0 1.25rem' }}>{t(`${CK}.success.createSubtitle`)}</p>
                    <RegistrationForm plan={plan} planName={planName} prefillEmail={email} paymentId={paymentId} billing={billing} />
                </div>
            </>
        );
    }

    return null;
}

export default PricingCheckout;
